/**
 * Ranked P2P fundraisers by amount raised for a campaign.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { LeaderboardEntry } from "@/hooks/useCampaignDonations"

export interface FundraiserLeaderboardProps {
  entries: LeaderboardEntry[]
}

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export function FundraiserLeaderboard({ entries }: FundraiserLeaderboardProps) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="fundraiser-leaderboard-empty">
        No peer fundraisers yet. Generate a link to get started.
      </p>
    )
  }

  return (
    <Table data-testid="fundraiser-leaderboard">
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">#</TableHead>
          <TableHead>Fundraiser</TableHead>
          <TableHead className="text-right">Raised</TableHead>
          <TableHead className="text-right">Personal goal</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((e, i) => {
          const pctPersonal =
            e.personalGoalCents && e.personalGoalCents > 0
              ? Math.min(100, Math.round((e.raisedCents / e.personalGoalCents) * 100))
              : null
          return (
            <TableRow key={e.fundraiserId}>
              <TableCell className="font-medium text-muted-foreground">{i + 1}</TableCell>
              <TableCell>{e.name}</TableCell>
              <TableCell className="text-right tabular-nums">{formatMoney(e.raisedCents)}</TableCell>
              <TableCell className="text-right text-muted-foreground text-sm">
                {e.personalGoalCents
                  ? `${formatMoney(e.personalGoalCents)}${pctPersonal != null ? ` (${pctPersonal}%)` : ""}`
                  : "—"}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
